import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { PickBatchStatusMessages } from '../../../../components/orders-grid/columns/warehouse-status';
import { getSalesOrderStatusFromInt, SalesOrderStatus } from '../../../../enums/sales-order-status';
import { usePusherEvent } from '../../../../hooks/use-pusher-event';
import { BULK_UPDATE_JOB_UPDATE } from '../../../../services/pusher-service/pusher-contract-messages';
import { stopPickingOrders, validateOrder } from '../pick-batch.api';
import { PICK_BATCH_STATUS, Steps } from './constants';
import { messages } from '../messages';

/**
 * @typedef {import('../pick-batch.api').PickBatchValidateResponse} PickBatchValidateResponse
 */

/**
 * @param {PickBatchValidateResponse} order
 * @param {object} intl
 * @returns {StatusProblemData|undefined} - Returns an object with problem data or undefined if no problem
 */
const getPickingProblemData = ({ pickBatchStatusName, orderStatusId: orderStatusName }, intl) => {
  const isAlreadyPicking = !!pickBatchStatusName;
  if (isAlreadyPicking) {
    const isPicked = pickBatchStatusName === PICK_BATCH_STATUS.PICKED;

    const orderStatus = getSalesOrderStatusFromInt(orderStatusName);
    const isShipped = orderStatus === SalesOrderStatus.Shipped;

    const pickBatchStatusNameText = intl.formatMessage(PickBatchStatusMessages[pickBatchStatusName]) ?? pickBatchStatusName;
    return {
      isWarning: !(isShipped && isPicked), // If the order is picked and shipped, this is an error, otherwise allow the user to reset the picking status
      problemDescription: intl.formatMessage(messages.orderPickingError, {
        pickingStatusName: pickBatchStatusNameText,
      }),
    };
  }
};

/**
 * @param {PickBatchValidateResponse} order
 * @param {object} intl
 * @returns {StatusProblemData|undefined} - Returns an object with problem data or undefined if no problem
 */
const getStatusProblemData = ({ orderStatusId: orderStatusName }, intl) => {
  const problemFreeStatuses = [SalesOrderStatus.AwaitingShipment];
  const errorStatuses = [SalesOrderStatus.PendingFulfillment, SalesOrderStatus.RejectedFulfillment, SalesOrderStatus.Cancelled];

  const orderStatus = getSalesOrderStatusFromInt(orderStatusName);

  if (problemFreeStatuses.includes(orderStatus)) {
    return;
  }
  const orderStatusText = intl.formatMessage(orderStatus.translation);
  const problemDescription = intl.formatMessage(messages.orderStatusError, {
    orderStatus: orderStatusText,
  });
  return {
    isWarning: !errorStatuses.includes(orderStatus),
    problemDescription,
  };
};

/**
 * @param {PickBatchValidateResponse} order
 * @param {object} intl
 * @returns {StatusProblemData|undefined} - Returns an object with problem data or undefined if no problem
 */
const getBatchProblemData = ({ fulfillmentPlanSetId }, intl) => {
  const isInOtherBatch = !!fulfillmentPlanSetId;
  if (isInOtherBatch) {
    return {
      isWarning: true,
      problemDescription: intl.formatMessage(messages.orderBatchError),
    };
  }
};

/**
 * @param {PickBatchValidateResponse} order
 * @param {object} intl
 * @returns {StatusProblemData} - Returns an object with problem data, or default values if no problem
 */
const getOrderProblemData = (order, intl) => {
  const pickingProblemData = getPickingProblemData(order, intl);
  if (pickingProblemData) {
    return pickingProblemData;
  }
  const statusProblemData = getStatusProblemData(order, intl);
  if (statusProblemData) {
    return statusProblemData;
  }
  const batchProblemData = getBatchProblemData(order, intl);
  if (batchProblemData) {
    return batchProblemData;
  }

  // Return default values when no problems are found. Records without `problemDescription`s are ignored later
  return { isWarning: false, problemDescription: undefined };
};

/**
 * @typedef {object} OrderValidationErrors
 * @property {string} id - order id
 * @property {string} orderDate
 * @property {string} problemDescription
 * @property {string} salesOrderNumber
 * @property {string} fulfillmentPlanId
 * @property {number} orderStatusId
 * @property {boolean} isWarning
 * @property {string|null} pickBatchStatusName
 */

/**
 * @param {Array<string>} fulfillmentPlanIds
 * @param {boolean} isBatch
 * @param {object} intl
 * @returns {Promise<Array<OrderValidationErrors>>}
 */
const checkOrderValidationErrors = async (fulfillmentPlanIds, isBatch, intl) => {
  /** @type {Array<PickBatchValidateResponse>} */
  const validateBatchResponse = await validateOrder(fulfillmentPlanIds, isBatch).then((res) => res.data);

  const mappedProblemOrders = validateBatchResponse
    .map((order) => {
      const { isWarning, problemDescription } = getOrderProblemData(order, intl);
      return {
        id: order.fulfillmentPlanId,
        orderDate: order.orderCreatedDate,
        problemDescription,
        salesOrderNumber: order.orderNumber,
        fulfillmentPlanId: order.fulfillmentPlanId,
        orderStatusId: order.orderStatusId,
        isWarning,
        pickBatchStatusName: order.pickBatchStatusName,
      };
    })
    .filter((order) => order.problemDescription);
  return mappedProblemOrders;
};

/**
 * @typedef {object} VerifyOrdersState
 * @property {boolean} canVerifyOrdersContinue
 * @property {() => Promise<void>} onVerifyOrdersContinue
 * @property {() => Promise<void>} prepareVerifyOrdersStep
 */

/**
 * @param {object} params
 * @param {(fpids: Array<string>, shipFrom: number) => Promise<string>} params.bulkUpdateShipFrom
 * @param {Array<string>} params.currentFulfillmentPlanIds
 * @param {boolean} params.inventoryTrackingIsEnabled
 * @param {boolean} params.isBatch
 * @param {() => Promise<void>} params.prepareVerifyInventoryStep
 * @param {Array<OrderValidationErrors>} params.problemOrders
 * @param {(number) => void} params.setCurrentStep
 * @param {(boolean) => void} params.setIsLoading
 * @param {(problemOrders: Array<OrderValidationErrors>) => void} params.setProblemOrders
 * @param {number} params.shipFromId
 * @returns {VerifyOrdersState}
 */
export const useVerifyOrdersState = ({
  bulkUpdateShipFrom,
  currentFulfillmentPlanIds,
  inventoryTrackingIsEnabled,
  isBatch,
  prepareVerifyInventoryStep,
  problemOrders,
  setCurrentStep,
  setIsLoading,
  setProblemOrders,
  shipFromId,
}) => {
  const intl = useIntl();
  const [batchJobId, setBatchJobId] = useState();

  const processBulkUpdate = useCallback(
    async (pusherMessage) => {
      if (!batchJobId || pusherMessage?.messageGuid !== batchJobId) {
        return;
      }
      const { data } = pusherMessage;
      const { isComplete, succeeded } = data || {};
      if (!isComplete) {
        return;
      }
      if (!succeeded) {
        // If the bulk update failed, we should not proceed to the next step.
        // Setting isLoading to false will re-enable the verify orders and allow the user to retry.
        setIsLoading(false);
        return;
      }
      setCurrentStep(Steps.checkForInventoryErrors);
      setIsLoading(false);
      // If the bulk update succeeded, we can proceed to the next step
      await prepareVerifyInventoryStep();
    },
    [batchJobId, prepareVerifyInventoryStep, setCurrentStep, setIsLoading],
  );
  usePusherEvent(BULK_UPDATE_JOB_UPDATE, processBulkUpdate);

  return useMemo(() => {
    const onVerifyOrdersContinueStart = async () => {
      // Check if there are any orders with PickBatch statuses that need to be reset
      const ordersWithPickBatchStatusWarnings = problemOrders.filter((order) => order.pickBatchStatusName);
      if (ordersWithPickBatchStatusWarnings.length > 0) {
        const fulfillmentPlanIdsToReset = ordersWithPickBatchStatusWarnings.map((order) => order.fulfillmentPlanId);
        await stopPickingOrders(fulfillmentPlanIdsToReset);
      }

      if (!inventoryTrackingIsEnabled) {
        // If inventory tracking is not enabled, there is no verify inventory step and we can skip to completing the modal
        setCurrentStep(Steps.complete);
        return;
      }
      // Initiate the bulk update of ship from locations for allocation
      // Done in `onVerifyOrdersContinue` so that if the update fails, we can retry by reattempting to complete this step
      setIsLoading(true);
      const jobGuid = await bulkUpdateShipFrom(currentFulfillmentPlanIds, shipFromId);
      if (jobGuid) {
        setBatchJobId(jobGuid);
      } else {
        // Failing to initiate the bulk update is one of the failure cases
        setIsLoading(false);
      }
    };
    const prepareVerifyOrdersStep = async () => {
      setIsLoading(true);
      const orderValidationErrors = await checkOrderValidationErrors(currentFulfillmentPlanIds, isBatch, intl);
      if (orderValidationErrors.length === 0) {
        // Nothing to do in Verify Orders step, complete that step
        await onVerifyOrdersContinueStart();
        return;
      }
      setProblemOrders(orderValidationErrors);
      setIsLoading(false);
    };
    const areAllProblemsWarnings = problemOrders.every((order) => order.isWarning);
    const canVerifyOrdersContinue = areAllProblemsWarnings;
    return {
      canVerifyOrdersContinue,
      onVerifyOrdersContinue: onVerifyOrdersContinueStart,
      prepareVerifyOrdersStep,
    };
  }, [
    bulkUpdateShipFrom,
    currentFulfillmentPlanIds,
    intl,
    inventoryTrackingIsEnabled,
    isBatch,
    problemOrders,
    setCurrentStep,
    setIsLoading,
    setProblemOrders,
    shipFromId,
  ]);
};
